"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _ExpandCollapse = _interopRequireDefault(require("../ExpandCollapse"));
var _ListboxGroup = _interopRequireDefault(require("./ListboxGroup"));
var _ListboxItem = _interopRequireDefault(require("./ListboxItem"));
var _ListboxContext = require("./ListboxContext");
var _ListboxOverlay = _interopRequireDefault(require("./ListboxOverlay"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const styles = _StyleSheet.default.create({
  list: {
    padding: 0,
    margin: 0
  }
});
const getInitialOpen = (items, selectedId) => items.filter(item => item.items && item.items.some(nestedItem => (nestedItem.id ?? nestedItem.label) === selectedId)).map(item => item.id ?? item.label);
const Listbox = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    items = [],
    firstItemRef = null,
    // focus will be moved to this one once within the menu
    parentRef = null,
    // to return focus to after leaving the last menu item
    selectedId: defaultSelectedId,
    LinkRouter,
    itemRouterProps,
    onClose,
    variant,
    tokens
  } = _ref;
  const initialOpen = getInitialOpen(items, defaultSelectedId);
  const [selectedId, setSelectedId] = _react.default.useState(defaultSelectedId);
  const {
    minHeight,
    minWidth
  } = (0, _ThemeProvider.useThemeTokens)('Listbox', variant, tokens);

  // We need to keep track of each item's ref in order to be able to
  // focus on a specific item via keyboard navigation
  const itemRefs = _react.default.useRef([]);
  if (firstItemRef?.current) itemRefs.current[0] = firstItemRef.current;
  const [focusedIndex, setFocusedIndex] = _react.default.useState(0);
  const handleKeydown = _react.default.useCallback(event => {
    const nextItemRef = itemRefs.current[focusedIndex + 1];
    const prevItemRef = itemRefs.current[focusedIndex - 1];
    if (event.key === 'ArrowUp' || event.shiftKey && event.key === 'Tab') {
      // Move the focus to the previous item or to the parent one if on the first
      if (prevItemRef) {
        event.preventDefault();
        prevItemRef.focus();
      } else if (parentRef) parentRef.current?.focus();
      setFocusedIndex(focusedIndex - 1);
    } else if ((event.key === 'ArrowDown' || event.key === 'Tab') && nextItemRef) {
      event.preventDefault();
      setFocusedIndex(focusedIndex + 1);
      nextItemRef.focus();
    } else if (event.key === 'Escape') {
      // Close the dropdown
      parentRef?.current?.click();
      // Return focus to the dropdown control after leaving the last item
      parentRef?.current?.focus();
      if (onClose) onClose(event);
    } else if (!nextItemRef && firstItemRef) {
      // If the last item is focused, move the focus to the first one
      event.preventDefault();
      setFocusedIndex(0);
      firstItemRef.current?.focus();
    }
  }, [focusedIndex, onClose, parentRef, firstItemRef]);

  // Add listeners for mouse clicks outside and for key presses
  _react.default.useEffect(() => {
    if (_Platform.default.OS === 'web') {
      window.addEventListener('click', onClose);
      window.addEventListener('keydown', handleKeydown);
      window.addEventListener('touchstart', onClose);
      return () => {
        window.removeEventListener('click', onClose);
        window.removeEventListener('keydown', handleKeydown);
        window.removeEventListener('touchstart', onClose);
      };
    }
    return () => {};
  }, [onClose, handleKeydown]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_ListboxContext.ListboxContext.Provider, {
    value: {
      selectedId,
      setSelectedId
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_ExpandCollapse.default, {
      initialOpen: initialOpen,
      maxOpen: 1,
      ref: ref,
      children: expandProps => /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: [styles.list, {
          minHeight,
          minWidth
        }],
        role: "listbox",
        children: items.map((item, index) => {
          const {
            id,
            label,
            items: nestedItems
          } = item;
          const itemId = id ?? label;

          // Give the list of refs.
          const itemRef = currentItemRef => {
            itemRefs.current[index] = currentItemRef;
            return currentItemRef;
          };
          return nestedItems ? /*#__PURE__*/(0, _react.createElement)(_ListboxGroup.default, {
            ...item,
            expandProps: expandProps,
            LinkRouter: LinkRouter,
            itemRouterProps: itemRouterProps,
            prevItemRef: itemRefs.current[index - 1] ?? null,
            nextItemRef: itemRefs.current[index + 1] ?? null,
            ref: index === 0 ? firstItemRef : itemRef,
            key: itemId
          }) : /*#__PURE__*/(0, _react.createElement)(_ListboxItem.default, {
            ...item,
            key: itemId,
            id: itemId,
            LinkRouter: LinkRouter,
            itemRouterProps: itemRouterProps,
            prevItemRef: itemRefs.current[index - 1] ?? null,
            nextItemRef: itemRefs.current[index + 1] ?? null,
            ref: index === 0 ? firstItemRef : itemRef
          });
        })
      })
    })
  });
});
Listbox.displayName = 'Listbox';
Listbox.propTypes = {
  ..._utils.withLinkRouter.propTypes,
  tokens: (0, _utils.getTokensPropType)('Listbox'),
  /**
   * Focus will be moved to the item with this ref once within the menu.
   */
  firstItemRef: _propTypes.default.object,
  /**
   * Focus will be returned to the dropdown control with this ref after leaving
   * the last menu item.
   */
  parentRef: _propTypes.default.object,
  /**
   * `Listbox` items
   */
  items: _propTypes.default.array,
  /**
   * To select an item by default
   */
  selectedId: _propTypes.default.string,
  /**
   * onClose event
   */
  onClose: _propTypes.default.func
};
Listbox.Overlay = _ListboxOverlay.default;
var _default = exports.default = Listbox;